/**
 * Central table of user-facing labels for every backend enum we surface. Keep
 * this ONE file authoritative so a rename in `packages/shared` propagates to
 * every pill/badge/table/drawer via one lookup instead of ad-hoc humanisation
 * (`str.replace('_', ' ')`) sprinkled across the codebase.
 *
 * Pattern: `<ENUM>_LABEL` maps a raw enum key to its display string. Call
 * sites should route through `humanEnum(map, value)` so an unknown value
 * degrades to a title-cased version of the raw key instead of `undefined`.
 */

// Itinerary lifecycle - `ItineraryStatusSchema` in packages/shared.
export const ITINERARY_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  IN_PROGRESS: 'In progress',
  SENT_TO_CUSTOMER: 'Sent to customer',
  REVISED: 'Revised',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  CONVERTED_TO_BOOKING: 'Converted to booking',
  CANCELLED: 'Cancelled',
};

// Follow-up outcomes - `LeadFollowUpResult` in packages/shared.
export const FOLLOW_UP_RESULT_LABEL: Record<string, string> = {
  SCHEDULED: 'Scheduled',
  CONNECTED: 'Connected',
  NO_RESPONSE: 'No response',
  DELAYED: 'Delayed',
  COMPLETED: 'Completed',
  CANCELED: 'Cancelled',
  CANCELLED: 'Cancelled',
};

// Lead-approval workflow - `LeadStatusChangeRequestStatusSchema`.
export const LEAD_APPROVAL_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  SUPERSEDED: 'Superseded',
};

// Notification delivery states.
export const NOTIFICATION_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  QUEUED: 'Queued',
  SENT: 'Sent',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
};

// Website publish workflow (blogs, pages, packages, destination pages, etc.).
export const WEBSITE_PUBLISH_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING_REVIEW: 'In review',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
};

// Lead-import job phases.
export const LEAD_IMPORT_STATUS_LABEL: Record<string, string> = {
  UPLOADED: 'Uploaded',
  VALIDATING: 'Validating',
  READY: 'Ready',
  PARTIAL: 'Partial',
  COMMITTING: 'Committing',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
};

// Lead priority level.
export const LEAD_PRIORITY_LABEL: Record<string, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  URGENT: 'Urgent',
};

/**
 * Resolve a raw enum value to its human label, falling back to a title-cased
 * version of the value when the map has no entry. Use this at every call site
 * that renders an enum so a rename can never silently leak the underscore-cased
 * key into the UI.
 */
export function humanEnum(map: Record<string, string>, value: string | null | undefined): string {
  if (!value) return '';
  const known = map[value];
  if (known) return known;
  return String(value)
    .toLowerCase()
    .split('_')
    .map((s, i) => (i === 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s))
    .join(' ');
}
